/**
 * Supplies the public mobile-rail nav tabs and derives the currently-active tab
 * slug from `?tab=<slug>` in the URL.
 *
 * Behaviour:
 *   - Prefers an SSR SEED delivered through `ProductNavTabsSeedContext` (see
 *     `components/products/nav/ProductNavTabsSeed.tsx`). When the seed covers the
 *     requested scope the hook returns it immediately, with `loading = false`,
 *     and makes NO request.
 *   - Falls back to fetching `/api/product-nav-tabs` on mount for any caller
 *     that has no seed. A module-level cache (60s TTL) dedupes fetches across
 *     rerenders and across instances on the same page — the rail may re-mount
 *     during layout swaps and we'd rather not re-hit D1.
 *   - The server already sends `Cache-Control: public, s-maxage=60` so
 *     the edge serves most requests; this hook's TTL matches to avoid
 *     stale-while-local-fresh mismatches.
 *   - Returns `{ tabs, activeSlug, loading }`. An empty `tabs` array is
 *     surfaced so the caller can short-circuit rendering.
 *
 * ── WHY THE SEED EXISTS (QNAVSEED) ──────────────────────────────────────────
 * This hook used to fetch UNCONDITIONALLY with no server seed, unlike the eight
 * sibling props `/products` resolves server-side. The first render therefore had
 * no tabs at all, so `/products` — a primary commercial route — served an empty
 * `aria-hidden` spacer where the rail belongs and shipped ZERO crawlable hrefs
 * for the quick-nav. The seed makes the first render (and the crawled HTML) real.
 */

"use client";

import { createContext, use, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";

export interface PublicNavTab {
  slug: string;
  label: string;
  iconName: string;
  sortOrder: number;
  /** Hex (e.g. "#ec4899") — present on colour-filter tabs. */
  displayColour?: string;
}

interface TabsResponse {
  tabs?: PublicNavTab[];
}

const CACHE_TTL_MS = 60_000;
/** Sentinel cache key for "no scope" (the root /products page). */
const GLOBAL_KEY = "__global__";

interface CacheEntry {
  tabs: PublicNavTab[];
  fetchedAt: number;
  /** In-flight promise so concurrent mounts share one request. */
  inflight: Promise<PublicNavTab[]> | null;
}

/**
 * Per-scope cache. Keyed on the scope slug (or `__global__` when no
 * scope is set). Each collection landing page has its own cache slot
 * so flipping between /products/fresh and /products/accessories doesn't
 * fall back to the wrong tab list.
 */
const cache = new Map<string, CacheEntry>();

function cacheKey(scope: string | null): string {
  return scope && scope.trim() ? scope.trim().toLowerCase() : GLOBAL_KEY;
}

function freshEntry(): CacheEntry {
  return { tabs: [], fetchedAt: 0, inflight: null };
}

async function fetchTabs(scope: string | null): Promise<PublicNavTab[]> {
  const key = cacheKey(scope);
  const entry = cache.get(key) ?? freshEntry();
  const now = Date.now();
  if (entry.tabs.length > 0 && now - entry.fetchedAt < CACHE_TTL_MS) {
    return entry.tabs;
  }
  if (entry.inflight) return entry.inflight;

  const url = scope
    ? `/api/product-nav-tabs?scope=${encodeURIComponent(scope)}`
    : "/api/product-nav-tabs";

  const p = (async () => {
    try {
      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok) return [];
      const data = (await res.json()) as TabsResponse;
      const tabs = Array.isArray(data.tabs) ? data.tabs : [];
      cache.set(key, { tabs, fetchedAt: Date.now(), inflight: null });
      return tabs;
    } catch {
      // Non-fatal — rail just doesn't render.
      cache.set(key, { tabs: [], fetchedAt: Date.now(), inflight: null });
      return [];
    }
  })();
  cache.set(key, { ...entry, inflight: p });
  return p;
}

/**
 * Server-resolved tab list for ONE scope, handed down through context.
 *
 * Context — not a zustand store and not a module cache — because this is
 * server-derived, subtree-scoped state that must be correct on the FIRST render.
 * A store is a module singleton whose initial value is fixed at module eval, so
 * it cannot see a server value until something writes it after mount, and on
 * Workers the isolate is shared across requests so writing one from a render is
 * a cross-request leak. See `.claude/rules/state-management.md`.
 */
export interface ProductNavTabsSeed {
  /** The scope the seed was resolved for — `null` on the `/products` root. */
  scope: string | null;
  tabs: PublicNavTab[];
}

/** `null` = no seed available; the hook then fetches, exactly as before. */
export const ProductNavTabsSeedContext = createContext<ProductNavTabsSeed | null>(
  null
);

export interface UseProductNavTabsOptions {
  /**
   * Optional collection scope — pass the active category slug when the
   * rail is rendered on a collection landing page (e.g. "accessories").
   * Omit / pass null on the `/products` root.
   */
  scope?: string | null;
}

export interface UseProductNavTabsResult {
  tabs: PublicNavTab[];
  activeSlug: string | null;
  loading: boolean;
}

/** Clear the module cache. Exported for tests. */
export function __resetNavTabsCacheForTests(): void {
  cache.clear();
}

export function useProductNavTabs(
  opts: UseProductNavTabsOptions = {}
): UseProductNavTabsResult {
  const searchParams = useSearchParams();
  const scope = opts.scope ?? null;
  const key = cacheKey(scope);

  // QNAVSEED — an SSR seed for THIS scope wins over anything else. `[]` is a
  // legitimate seed (the admin has configured no tabs), so the presence test is
  // `!== null`, never `.length > 0`.
  const seed = use(ProductNavTabsSeedContext);
  const seededTabs =
    seed !== null && cacheKey(seed.scope) === key ? seed.tabs : null;

  const [fetchedTabs, setTabs] = useState<PublicNavTab[]>(() => {
    const entry = cache.get(key);
    return entry && entry.tabs.length > 0 && Date.now() - entry.fetchedAt < CACHE_TTL_MS
      ? entry.tabs
      : [];
  });
  const [fetching, setLoading] = useState<boolean>(() => {
    const entry = cache.get(key);
    return !(entry && entry.tabs.length > 0 && Date.now() - entry.fetchedAt < CACHE_TTL_MS);
  });

  useEffect(() => {
    // Seeded: the server already resolved this list into the HTML, so a mount
    // fetch would be a redundant round-trip for data the page is already
    // holding. The fetch stays as the RECOVERY path for unseeded callers — the
    // same shape `ProductsClient` uses for its categories/colours seeds.
    if (seededTabs !== null) return;
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- revalidate the derived cache freshness whenever the requested navigation scope changes.
    setLoading((prev) => {
      const entry = cache.get(key);
      const fresh = entry && entry.tabs.length > 0 && Date.now() - entry.fetchedAt < CACHE_TTL_MS;
      return fresh ? false : prev;
    });
    void fetchTabs(scope).then((next) => {
      if (cancelled) return;
      setTabs(next);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // `scope` is the only input that changes the URL; `key` is derived, and
    // `seededTabs` is a per-scope guard whose identity changes on every server
    // render — listing it would re-run the effect for no behavioural gain.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // Derived, NOT copied into state: a seed that changes (a Link navigation
  // re-renders the server page) must reach the rail, and a `useState` seeded
  // once at mount would pin the first value for the whole session.
  const tabs = seededTabs ?? fetchedTabs;
  const loading = seededTabs !== null ? false : fetching;

  const activeSlug = useMemo(() => {
    const raw = searchParams.get("tab");
    if (!raw) return null;
    // Only consider the active slug "active" if it matches a known tab —
    // a stale URL with a removed slug shouldn't highlight anything.
    return tabs.some((t) => t.slug === raw) ? raw : null;
  }, [searchParams, tabs]);

  return { tabs, activeSlug, loading };
}
